import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { ILanguage } from '@/types/task.interface'
import type {
	ISelectedPlatform,
	TPlatformValue
} from '@/types/uploadForm.interface'

type TaskId = string
type TSelectedWay = string

interface FormUploaderState {
	selectedPlatformByTaskId: Record<TaskId, TPlatformValue | null>
	selectedPlatformListByTaskId: Record<TaskId, ISelectedPlatform[]>
	toFollowByTaskId: Record<TaskId, boolean>
	uploadingByTaskId: Record<TaskId, boolean>
	transcribingByTaskId: Record<TaskId, boolean>
	selectedWayByTaskId: Record<TaskId, TSelectedWay>
	openFormModalByTaskId: Record<TaskId, boolean>
	selectedTranscribingLangByTaskId: Record<TaskId, ILanguage | null>
	pendingSrtFileNameByTaskId: Record<TaskId, string>
	uploadingFormByTaskId: Record<TaskId, boolean>
	uploadedFormByTaskId: Record<TaskId, boolean>
}

const initialState: FormUploaderState = {
	selectedPlatformByTaskId: {},
	selectedPlatformListByTaskId: {},
	toFollowByTaskId: {},
	uploadingByTaskId: {},
	transcribingByTaskId: {},
	selectedWayByTaskId: {},
	openFormModalByTaskId: {},
	selectedTranscribingLangByTaskId: {},
	pendingSrtFileNameByTaskId: {},
	uploadingFormByTaskId: {},
	uploadedFormByTaskId: {}
}

export const formUploaderSlice = createSlice({
	name: 'formUploader',
	initialState,
	reducers: {
		// Selected platform (single)
		setSelectedPlatform: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: TPlatformValue | null }>
		) => {
			state.selectedPlatformByTaskId[action.payload.taskId] =
				action.payload.value
		},

		resetSelectedPlatform: (
			state,
			action: PayloadAction<{ taskId: TaskId }>
		) => {
			delete state.selectedPlatformByTaskId[action.payload.taskId]
		},

		// Selected platform list
		setSelectedPlatformList: (
			state,
			action: PayloadAction<{ taskId: TaskId; list: ISelectedPlatform[] }>
		) => {
			state.selectedPlatformListByTaskId[action.payload.taskId] =
				action.payload.list
		},

		// Patch for single platform in the selectedPlatformList
		patchSelectedPlatform: (
			state,
			action: PayloadAction<{
				taskId: TaskId
				value: TPlatformValue
				patch: Partial<ISelectedPlatform>
			}>
		) => {
			const list = state.selectedPlatformListByTaskId[action.payload.taskId]
			if (!list) return

			const platform = list.find(p => p.value === action.payload.value)
			if (!platform) return

			Object.assign(platform, action.payload.patch)
		},

		resetSelectedPlatformList: (
			state,
			action: PayloadAction<{ taskId: TaskId }>
		) => {
			delete state.selectedPlatformListByTaskId[action.payload.taskId]
		},

		// Actions for adding and deleting
		addSelectedPlatformToList: (
			state,
			action: PayloadAction<{ taskId: TaskId; item: ISelectedPlatform }>
		) => {
			const prev =
				state.selectedPlatformListByTaskId[action.payload.taskId] || []
			if (prev.some(p => p.value === action.payload.item.value)) return
			state.selectedPlatformListByTaskId[action.payload.taskId] = [
				...prev,
				action.payload.item
			]
		},

		removeSelectedPlatformFromList: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: TPlatformValue }>
		) => {
			const prev =
				state.selectedPlatformListByTaskId[action.payload.taskId] || []
			state.selectedPlatformListByTaskId[action.payload.taskId] = prev.filter(
				p => p.value !== action.payload.value
			)
		},

		// open form modal

		setOpenFormModal: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: boolean }>
		) => {
			state.openFormModalByTaskId[action.payload.taskId] = action.payload.value
		},

		resetOpenFormModal: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.openFormModalByTaskId[action.payload.taskId]
		},

		// set selectedWay

		setSelectedWay: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: TSelectedWay }>
		) => {
			state.selectedWayByTaskId[action.payload.taskId] = action.payload.value
		},

		resetSelectedWay: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.selectedWayByTaskId[action.payload.taskId]
		},

		// selected transcribing language

		setSelectedTranscribingLang: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: ILanguage | null }>
		) => {
			state.selectedTranscribingLangByTaskId[action.payload.taskId] =
				action.payload.value
		},

		resetSelectedTranscribingLang: (
			state,
			action: PayloadAction<{ taskId: TaskId }>
		) => {
			delete state.selectedTranscribingLangByTaskId[action.payload.taskId]
		},

		// set toFollow option
		setToFollow: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: boolean }>
		) => {
			state.toFollowByTaskId[action.payload.taskId] = action.payload.value
		},

		resetToFollow: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.toFollowByTaskId[action.payload.taskId]
		},

		// pending SRT-file name

		setPendingSrtFileName: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: string }>
		) => {
			state.pendingSrtFileNameByTaskId[action.payload.taskId] =
				action.payload.value
		},

		resetPendingSrtFileName: (
			state,
			action: PayloadAction<{ taskId: TaskId }>
		) => {
			delete state.pendingSrtFileNameByTaskId[action.payload.taskId]
		},

		// uploading
		setUploading: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: boolean }>
		) => {
			state.uploadingByTaskId[action.payload.taskId] = action.payload.value
		},

		resetUploading: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.uploadingByTaskId[action.payload.taskId]
		},

		// transcribing
		setTranscribing: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: boolean }>
		) => {
			state.transcribingByTaskId[action.payload.taskId] = action.payload.value
		},

		resetTranscribing: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.transcribingByTaskId[action.payload.taskId]
		},

		// form uploading
		setFormUploading: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: boolean }>
		) => {
			state.uploadingFormByTaskId[action.payload.taskId] = action.payload.value
		},

		resetFormUploading: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.uploadingFormByTaskId[action.payload.taskId]
		},

		// form uploaded
		setFormUploaded: (
			state,
			action: PayloadAction<{ taskId: TaskId; value: boolean }>
		) => {
			state.uploadedFormByTaskId[action.payload.taskId] = action.payload.value
		},

		resetFormUploaded: (state, action: PayloadAction<{ taskId: TaskId }>) => {
			delete state.uploadedFormByTaskId[action.payload.taskId]
		}
	}
})

export const {
	setSelectedPlatform,
	resetSelectedPlatform,
	setSelectedPlatformList,
	patchSelectedPlatform,
	resetSelectedPlatformList,
	addSelectedPlatformToList,
	removeSelectedPlatformFromList,
	setOpenFormModal,
	resetOpenFormModal,
	setSelectedWay,
	resetSelectedWay,
	setSelectedTranscribingLang,
	resetSelectedTranscribingLang,
	setToFollow,
	resetToFollow,
	setPendingSrtFileName,
	resetPendingSrtFileName,
	setUploading,
	resetUploading,
	setTranscribing,
	resetTranscribing,
	setFormUploading,
	resetFormUploading,
	setFormUploaded,
	resetFormUploaded
} = formUploaderSlice.actions

export const formUploaderReducer = formUploaderSlice.reducer
